import { CARD_H, CARD_W, LIVE_INPUT_MAX_DIM } from "@webscan/constants.js";
import type { LetterboxedFrame, PoseDetection } from "./pose";
import { windowTransform, letterboxSurface } from "./letterbox-canvas";
import type { SourceWindow, ViewRect } from "../shared/geometry";
import type { PixelSource } from "./camera-frame";
import { thumbnailIndices } from "./swap-thumbnail";

/**
 * Reading things out of a camera frame: the 2D-canvas samplers the scan loop falls back
 * on when there is no GL context, and the cheap measurements it takes on what they
 * produce — a thumbnail to prove a card is the same card, a focus score to know whether
 * a crop is worth identifying, and the raster size a card deserves to be warped from.
 *
 * Canvases here are compute surfaces, never attached to the page; nothing in this file
 * is drawn for anyone to see.
 */

/** One frame, already at the size the stage that asked for it wants. */
export interface Raster {
  data: Uint8ClampedArray;
  width: number;
  height: number;
}

/**
 * Whether the readback canvases are CPU-backed.
 *
 * `willReadFrequently: true` asks the browser for a software canvas, on the theory that
 * we read it every frame. On iOS that is the wrong trade: the source is a *video*, whose
 * frames live in GPU memory, so a software canvas forces the whole 2160x3840 frame down
 * to the CPU and scales it there — 42.5 ms a frame, measured. GPU-backed, `drawImage`
 * scales on the GPU and only the 640x640 result is read back.
 *
 * `?cpucanvas` restores the old behaviour, because which side wins is a per-device
 * question and guessing it twice is worse than being able to compare.
 */
const CPU_CANVAS = /[?&]cpucanvas\b/.test(location.search);

/**
 * Draw the video into a canvas of a chosen size and read it back.
 *
 * The scaling is done by `drawImage` — on the GPU, into a canvas the size of the answer —
 * rather than by grabbing the sensor raster and resampling it in JavaScript. The rasters
 * handed downstream are identical in size to before (720 for detection, 1920 for the
 * warp) and the capture resolution is untouched; only the route changed.
 *
 * That route was the problem. A 4K `getImageData` is a fresh 33 MB `Uint8ClampedArray`
 * every iteration, and `resizeArea` returns a **Float64Array** — 9 MB for the detection
 * raster and 66 MB for the 1920 one, in a single allocation. About 44 MB per frame, some
 * 437 MB/s of garbage at 10 fps. Desktop absorbs it; iOS Safari kills the tab, with no
 * error to catch because the process is gone.
 *
 * One canvas per size, reused. `willReadFrequently` because every frame is read back.
 */
export function rasteriser() {
  let canvas: HTMLCanvasElement | null = null;
  let context: CanvasRenderingContext2D | null = null;
  return (video: PixelSource, window: SourceWindow, maxDim: number): Raster | null => {
    if (!window.sw || !window.sh) return null;

    // Never upscale: the window's own resolution is the ceiling on detail available.
    const scale = Math.min(1, maxDim / Math.max(window.sw, window.sh));
    const width = Math.max(1, Math.round(window.sw * scale));
    const height = Math.max(1, Math.round(window.sh * scale));
    if (!canvas || canvas.width !== width || canvas.height !== height) {
      canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      context = canvas.getContext("2d", { willReadFrequently: CPU_CANVAS });
      if (context) context.imageSmoothingQuality = "high";
    }
    if (!context) return null;
    context.setTransform(...windowTransform(window, 0, 0, width, height));
    context.drawImage(video, 0, 0);
    context.setTransform(1, 0, 0, 1, 0, 0);
    return { data: context.getImageData(0, 0, width, height).data, width, height };
  };
}

/**
 * Fit the scan region into the model's raster, on the GPU, in one draw.
 *
 * This replaces three CPU stages: reading back a separate detection raster, resampling it
 * in scalar JavaScript through a `Float64Array`, and copying the result into the padded
 * input. The canvas does the scale and the padding together, and the readback is the
 * model's own 640x640 — smaller than the intermediate it replaces.
 *
 * The arithmetic still follows ultralytics exactly, `round(d - 0.1)` included, because the
 * quads come back in this raster's pixels and any disagreement lands on the corners.
 *
 * `area` is the detection area within the window — the whole of it unless the user has
 * pulled the cropper in — and the quads therefore come back in the area's own pixels.
 */
export function letterboxer() {
  const surface = letterboxSurface(CPU_CANVAS);
  return (
    video: PixelSource,
    window: SourceWindow,
    modelWidth: number,
    modelHeight: number,
    area: ViewRect,
  ): LetterboxedFrame | null => {
    const drawn = surface.draw(video, window, modelWidth, modelHeight, area);
    return drawn
      ? {
          data: drawn.context.getImageData(0, 0, modelWidth, modelHeight).data,
          scale: drawn.scale,
          left: drawn.left,
          top: drawn.top,
        }
      : null;
  };
}

/**
 * A coarse grey thumbnail of whatever lies inside a quad, and how far two of them differ.
 *
 * This is the one question geometry cannot answer: after a gap in the evidence, is the
 * card in frame the card that was read, or a different one in the same spot? Content is
 * the only discriminator, and it only has to separate "the same picture again" from "a
 * different picture" — nothing here retrieves, verifies or identifies anything.
 *
 * So it is deliberately not a warp. The detector's own letterboxed raster is already in
 * hand, and 256 samples on the quad's bilinear parameterisation cost nothing measurable
 * next to the inference that produced it; a warp would mean a second GPU readback per gap
 * to answer a yes/no question. Sampled inside a margin because the corners are where the
 * detector's error lives, and mean-centred because auto-exposure routinely shifts the
 * whole frame between two readings of one motionless card — a brightness step is not a
 * different card, a different pattern is.
 */
/**
 * Mean absolute difference, over full scale, past which the picture has changed.
 *
 * Two readings of one resting card land a couple of percent apart — corner jitter and
 * sensor noise, with exposure already divided out. Two different cards land well into the
 * tens. On a synthetic raster with art detail finer than a card's: 2.2% mean and 3.9%
 * worst over +-3 px of corner jitter, unmoved by a 40-level exposure step, against 23.8%
 * for a different picture and 14.2% for the card being gone. The threshold sits in that
 * empty middle, and the measured value is logged on every comparison so it can be re-cut
 * from a real trace rather than from this reasoning.
 *
 * Erring high costs a missed card; erring low costs a duplicate read that the log then
 * suppresses. Those are not the same price, so this sits below the middle of the gap.
 *
 * What it cannot see is a card exchanged for *another printing of the same artwork*, which
 * at this resolution is the same picture — the set symbol and the collector number that
 * separate them are a few pixels each. Those swaps are caught the way every swap outside a
 * gap is: by the motion, or by the gap outlasting the grace window.
 */
export const SWAP_DIFFERENCE = 0.06;

export function quadThumb(
  frame: LetterboxedFrame,
  quad: Float32Array,
  area: ViewRect,
  rasterWidth: number,
  rasterHeight: number,
): Float32Array {
  const { data } = frame;
  const indices = thumbnailIndices(frame, quad, area, rasterWidth, rasterHeight);
  const out = new Float32Array(indices.length);
  let mean = 0;
  for (let i = 0; i < indices.length; i++) {
    const p = indices[i] * 4;
    const grey = (data[p] * 299 + data[p + 1] * 587 + data[p + 2] * 114) / 1000;
    out[i] = grey;
    mean += grey;
  }
  mean /= out.length;
  for (let i = 0; i < out.length; i++) out[i] -= mean;
  return out;
}

/**
 * How far two thumbnails differ, as a fraction of full scale.
 *
 * Taken as the better of the two 180-degree readings, which on a square grid is the
 * reversed index. The detector's corner order is semantic rather than positional, so a
 * card it reads upside down one frame and upright the next would otherwise compare as a
 * different card — a false swap costs a duplicate read, but there is no reason to pay it.
 */
export function thumbDifference(a: Float32Array, b: Float32Array): number {
  let direct = 0;
  let turned = 0;
  const n = a.length;
  for (let i = 0; i < n; i++) {
    direct += Math.abs(a[i] - b[i]);
    turned += Math.abs(a[i] - b[n - 1 - i]);
  }
  return Math.min(direct, turned) / n / 255;
}

/**
 * How sharp the warp is: gradient *energy* against the contrast carrying it.
 *
 * A phone hunting focus on a card that has just landed hands recognition a crop nothing
 * can identify, and until this existed every one of those cost an attempt — the budget was
 * spent on the blurred first second and the placement closed before the card was worth
 * reading. Measuring it separates "this card is unknown" from "this picture of it is
 * unreadable", which are opposite conclusions drawn from the same rejection.
 *
 * Squared differences, not absolute ones, and that is the whole trick. Mean |gradient| is
 * very nearly *invariant* to blur: smearing an edge over three pixels thirds each step and
 * triples the count, so the total variation — and its mean — survives. Measured on a
 * synthetic card it went the wrong way, scoring a blurred raster 1.5x its sharp original.
 * Squaring breaks that symmetry: a third of the step contributes a ninth of the energy, so
 * the sum falls with the smear. The same synthetic card scores 1.00 sharp, 0.53 under a
 * one-pixel blur, 0.29 at two and 0.07 at three.
 *
 * Divided by the variance of the same samples, because blur destroys the high frequencies
 * while leaving the low ones — and therefore the contrast — nearly intact. So the ratio
 * falls with defocus but not with a dark card, a bright one, or a card whose art is flat:
 * a 50-level exposure step and a halving of contrast both move it by about 1%.
 *
 * Gradients against the immediate neighbour, over every second pixel: the finest scale the
 * raster has is the one defocus takes first, while sampling half of them keeps it at about
 * 0.4 ms over the canonical card.
 */
export function focusScore(card: Uint8ClampedArray): number {
  const stride = 2;
  let sum = 0;
  let sumSquares = 0;
  let energy = 0;
  let count = 0;
  const luma = (p: number) => (card[p] * 299 + card[p + 1] * 587 + card[p + 2] * 114) / 1000;
  for (let y = 0; y < CARD_H - stride; y += stride) {
    for (let x = 0; x < CARD_W - stride; x += stride) {
      const p = (y * CARD_W + x) * 4;
      const here = luma(p);
      const dx = luma(p + 4) - here;
      const dy = luma(p + CARD_W * 4) - here;
      energy += dx * dx + dy * dy;
      sum += here;
      sumSquares += here * here;
      count++;
    }
  }
  if (!count) return 0;
  const mean = sum / count;
  const variance = Math.max(0, sumSquares / count - mean * mean);
  // The +1 keeps a blank crop — no contrast, no gradients — from dividing by nothing.
  return energy / count / (variance + 1);
}

/**
 * How large a raster to warp this particular card out of.
 *
 * A fixed 1920 cap wastes the sensor on exactly the cards that need it most. A card
 * filling the frame arrives at 488x680 with pixels to spare; a card 89 px tall in the
 * 720 detection raster is only 236 px tall at 1920 and gets stretched 2.9x to fill the
 * canonical raster — and every hash, and ORB, then reads that blur. The sensor had 3.3x
 * more detail available and we declined it.
 *
 * So the raster is chosen from the card: enough that it arrives at roughly its canonical
 * height without upscaling, capped at what the camera actually produces and never below
 * the old fixed size. Quantised, because the canvas is reallocated whenever the size
 * changes and a value that drifts with every frame would thrash it.
 *
 * This spends memory only on the settle, and only when the card is small — the case that
 * was otherwise unreadable.
 */
export function warpRasterFor(card: PoseDetection, crop: SourceWindow): number {
  const cropLongEdge = Math.max(crop.sw, crop.sh);
  // The quad is already in crop pixels; contourArea is width x height and aspect is w/h.
  const cardHeightInCrop = card.aspect > 0 ? Math.sqrt(card.area / card.aspect) : 0;
  if (cardHeightInCrop < 1) return Math.min(LIVE_INPUT_MAX_DIM, cropLongEdge);

  const wanted = (CARD_H / cardHeightInCrop) * cropLongEdge;
  const quantised = Math.ceil(wanted / 320) * 320;
  return Math.min(cropLongEdge, Math.max(LIVE_INPUT_MAX_DIM, quantised));
}
